import {
    Classes,
    DialogBody,
    FormGroup,
    H4,
    MultiSlider,
    RadioCard,
    RadioGroup
} from "@blueprintjs/core";
import classNames from "classnames";
import { MISO_WHITEPAPER_URL } from "in_queue/constants";
import { useStageConfig } from "in_queue/contexts/ClusterContext";
import {
    CostAssumption,
    CostAssumptionType,
    CustomCostAssumption,
    ScenarioStage
} from "in_queue/types/scenarioType";
import { useEffect } from "react";
import { ScenarioBuilderLabel } from "../ScenarioBuilder/scenarioBuilderParams";
import css from "./CostAssumptionsPanel.module.scss";

interface CostAssumptionCustomOptionsProps {
    costAssumptions: CustomCostAssumption;
    setCostAssumptions: (costAssumptions: CostAssumption) => void;
}

const CostAssumptionCustomOptions = ({
    costAssumptions,
    setCostAssumptions
}: CostAssumptionCustomOptionsProps) => {
    return (
        <div>
            <FormGroup
                label="Thresholds"
                subLabel="Define the thresholds at which lines are reconductored or rebuilt."
                style={{ margin: "0 5px" }}
            >
                <div style={{ margin: "0 3px" }}>
                    <MultiSlider
                        min={100}
                        max={200}
                        stepSize={1}
                        labelStepSize={50}
                    >
                        <MultiSlider.Handle
                            value={costAssumptions.reconductorLoadingLimit}
                            onChange={(evt) => {
                                setCostAssumptions({
                                    ...costAssumptions,
                                    reconductorLoadingLimit: evt
                                });
                            }}
                            interactionKind="push"
                            trackStyleBefore={{
                                backgroundColor: "#3DCC91"
                            }}
                            trackStyleAfter={{
                                backgroundColor: "#FFB366"
                            }}
                        />
                        <MultiSlider.Handle
                            value={costAssumptions.rebuildLoadingLimit}
                            onChange={(evt) => {
                                setCostAssumptions({
                                    ...costAssumptions,
                                    rebuildLoadingLimit: evt
                                });
                            }}
                            interactionKind="push"
                            trackStyleAfter={{
                                backgroundColor: "#FF7373"
                            }}
                        />
                    </MultiSlider>
                </div>
            </FormGroup>
            <div>
                <p>
                    <b>Reconductor</b> assumed for constraints between 100% and{" "}
                    {costAssumptions.reconductorLoadingLimit}% loading.
                </p>
                <p>
                    <b>Rebuild</b> assumed for constraints between{" "}
                    {costAssumptions.reconductorLoadingLimit}% and{" "}
                    {costAssumptions.rebuildLoadingLimit}% loading.
                </p>
                <p>
                    <b>New single-circuit line build</b> assumed for constraints
                    above {costAssumptions.rebuildLoadingLimit}% loading.
                </p>
            </div>
        </div>
    );
};

export const costAssumptionTypeToCostAssumption: Record<
    CostAssumptionType,
    CostAssumption
> = {
    [CostAssumptionType.CONSERVATIVE]: {
        type: CostAssumptionType.CONSERVATIVE
    },
    [CostAssumptionType.CUSTOM]: {
        type: CostAssumptionType.CUSTOM,
        reconductorLoadingLimit: 110.0,
        rebuildLoadingLimit: 150.0
    },
    [CostAssumptionType.NETWORK_UPGRADES]: {
        type: CostAssumptionType.NETWORK_UPGRADES
    },
    [CostAssumptionType.SUGAR_UPGRADES]: {
        type: CostAssumptionType.SUGAR_UPGRADES
    }
};

export const stageToDefaultCostAssumption: Record<
    ScenarioStage,
    CostAssumption
> = {
    PREVIEW: {
        type: CostAssumptionType.CONSERVATIVE
    },
    DECISION_POINT: {
        type: CostAssumptionType.NETWORK_UPGRADES
    }
};

type CostAssumptionLabels = {
    label: string;
    sublabel: string | React.ReactElement;
};

const COST_ASSUMPTION_TYPE_LABELS: Record<
    CostAssumptionType,
    CostAssumptionLabels
> = {
    [CostAssumptionType.CONSERVATIVE]: {
        label: "Conservative",
        sublabel: "Assume a new line build for every constraint."
    },
    [CostAssumptionType.CUSTOM]: {
        label: "Custom",
        sublabel: "Define custom cost assumptions based on percentage loading."
    },
    [CostAssumptionType.NETWORK_UPGRADES]: {
        label: "ISO-Allocated Costs",
        sublabel: "Use upgrade costs from system impact studies."
    },
    [CostAssumptionType.SUGAR_UPGRADES]: {
        label: "MISO Whitepaper Upgrades",
        sublabel: (
            <div>
                Network upgrade costs are determined using the process outlined
                in the{" "}
                <a href={MISO_WHITEPAPER_URL} target="_blank">
                    MISO Transmission Mitigation Selection and Cost Estimation
                    Approach Whitepaper
                </a>
                .
            </div>
        )
    }
};

interface CostAssumptionsPanelProps {
    costAssumptions: CostAssumption;
    setCostAssumptions: (costAssumptions: CostAssumption) => void;
    scenarioStage: ScenarioStage;
}

export const CostAssumptionsPanel = ({
    costAssumptions,
    setCostAssumptions,
    scenarioStage
}: CostAssumptionsPanelProps) => {
    const { supportedCostAssumptionTypes } = useStageConfig();

    const onlySugarUpgrades =
        supportedCostAssumptionTypes[scenarioStage].length === 1 &&
        supportedCostAssumptionTypes[scenarioStage].includes(
            CostAssumptionType.SUGAR_UPGRADES
        );

    useEffect(() => {
        if (onlySugarUpgrades) {
            setCostAssumptions({
                type: CostAssumptionType.SUGAR_UPGRADES
            });
        }
    }, []);

    if (onlySugarUpgrades) {
        return (
            <DialogBody className={css["CostAssumptionsPanel-body"]}>
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "10px"
                    }}
                >
                    <H4>{ScenarioBuilderLabel.CostAssumptions}</H4>
                    <div>
                        Network upgrade costs are determined using the process
                        outlined in the{" "}
                        <a href={MISO_WHITEPAPER_URL} target="_blank">
                            MISO Transmission Mitigation Selection and Cost
                            Estimation Approach Whitepaper
                        </a>
                        .
                    </div>
                </div>
            </DialogBody>
        );
    }

    return (
        <DialogBody className={css["CostAssumptionsPanel-body"]}>
            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px"
                }}
            >
                <H4>{ScenarioBuilderLabel.CostAssumptions}</H4>
                <CostAssumptionsPicker
                    costAssumptions={costAssumptions}
                    setCostAssumptions={setCostAssumptions}
                    scenarioStage={scenarioStage}
                />
            </div>
        </DialogBody>
    );
};

/** The cost assumption choices for a stage, without the step's frame and heading. */
export const CostAssumptionsPicker = ({
    costAssumptions,
    setCostAssumptions,
    scenarioStage
}: CostAssumptionsPanelProps) => {
    const { supportedCostAssumptionTypes } = useStageConfig();

    return (
        <>
            <RadioGroup
                className={css["CostAssumptionsPanel-cards"]}
                selectedValue={costAssumptions.type}
                onChange={(element) => {
                    const newCostAssumptionType = element.currentTarget
                        .value as CostAssumptionType;
                    setCostAssumptions(
                        costAssumptionTypeToCostAssumption[
                            newCostAssumptionType
                        ]
                    );
                }}
            >
                {supportedCostAssumptionTypes[scenarioStage].map(
                    (assumptionType: CostAssumptionType) => (
                        <RadioCard
                            key={assumptionType}
                            className={css["CostAssumptionsPanel-card"]}
                            compact
                            alignIndicator="left"
                            value={assumptionType}
                        >
                            {COST_ASSUMPTION_TYPE_LABELS[assumptionType].label}
                            <span
                                className={classNames(
                                    Classes.TEXT_MUTED,
                                    Classes.TEXT_SMALL
                                )}
                            >
                                {
                                    COST_ASSUMPTION_TYPE_LABELS[assumptionType]
                                        .sublabel
                                }
                            </span>
                        </RadioCard>
                    )
                )}
            </RadioGroup>
            {costAssumptions.type === CostAssumptionType.CUSTOM && (
                <CostAssumptionCustomOptions
                    costAssumptions={costAssumptions}
                    setCostAssumptions={setCostAssumptions}
                />
            )}
        </>
    );
};
